<div class="its-topics-action__options">
    {#if config.canEdit}
        <div class="copy-topics-modal">
            <Modal
                id="show-copy-topics-modal-id"
                bind:enabled={showCopyTopicsModal}
                titleText={i18n.copy}
                {body}
                confirmText={i18n.copy}
                cancelText={i18n.cancel}
                on:close={copyTopicsModalClicked}
            >
                <div slot="body">
                    <form>
                        <fieldset>
                            <legend>{i18n.copyTheSelectedTopicsTo}</legend>

                            <ButtonDropdown
                                disabled={dropDownCoursesLoading}
                                iconClass={dropDownCoursesLoading ? 'prom-button--loading' : 'prom-button-dropdown-icon'}
                                iconPlacement="end"
                                id="copy-topics-modal__courses-dropdown"
                                textOnly={false}
                                text={coursesDropdownText}
                                responsive={false}
                                items={coursesItems}
                                type="secondary"
                                on:beforeToggle={loadCourses}
                                on:beforeToggle={addScrollCourseDropdownTopics}
                                on:selectedOption={selectCourse}
                            />
                            <div
                                class="copy-topics-modal__error"
                                hidden={errorNoCourseSelected ? "" : "hidden"}
                            >
                                {i18n.selectACourse}
                            </div>
                        </fieldset>
                        {#if courseIsSelected}
                            <fieldset>
                                <legend>{i18n.options}</legend>

                                {#if isDateVisible()}
                                    <div class="copy-topics-modal__options-checkbox">
                                        <Checkbox
                                            checked={checkedCopyDates}
                                            label={i18n.copyDatesForNewPlans}
                                            small
                                            on:change={updateCheckedStatusCopyDates}
                                        />
                                    </div>
                                {/if}
                                <div class="copy-topics-modal__options-checkbox">
                                    <Checkbox
                                        checked={checkedConnectedResources}
                                        label={i18n.copyConnectedResources}
                                        small
                                        on:change={updateCheckedStatusConnectedResources}
                                    />
                                    <div class="copy-topics-modal__options-checkbox-info">
                                        {i18n.copyConnectedResourcesInfoMessage}
                                    </div>
                                </div>
                                {#if showAssessmentCriteriaSection}
                                    <div class="copy-topics-modal__options-checkbox">
                                        <Checkbox
                                            checked={checkedAssessmentCriteria}
                                            label={i18n.replaceExistingAssesmentCriteria}
                                            small
                                            on:change={updateCheckedStatusAssessmentCriteria}
                                        />
                                    </div>
                                {/if}
                            </fieldset>
                        {/if}
                    </form>
                </div>
            </Modal>
        </div>

        <Modal
            id="copy-in-progress-modal-id"
            bind:enabled={showCopyInProgress}
            titleText={i18n.copy}
            confirmText={i18n.close}
            showCancel={false}
        >
            <div slot="body">
                <div class="copy-topics__copy-in-progress">
                    <span class="screen-reader">{i18n.copyInProgress}</span>
                </div>
            </div>
        </Modal>

        <CopyConfirmAndErrorModal
            {showCopyConfirmModal}
            {i18n}
            {confirmModalBody}
            bannerVisible={confirmModalBannerVisibility}
            bannerText={confirmModalBannerText}
            on:close={closeCopyTopicsConfirmModalClicked}
        />

        <Modal
            id="show-delete-topics-confirm-modal-id"
            bind:enabled={showDeleteTopicsConfirmModal}
            titleText={config.deleteTopicHeading}
            bodyHtml={i18n.deleteTopicConfirmDialogPart1_Text +
                "<br>" +
                i18n.deleteTopicConfirmDialogPart2_Text}
            confirmText={i18n.confirm}
            cancelText={i18n.cancel}
            confirmButtonType="destructive"
            on:close={deleteTopicsModalClicked}
        />

        <Modal
            id="no-topics-selected-modal-id"
            bind:enabled={showNoTopicsSelectedModal}
            showCancel={false}
            confirmText={i18n.ok}
            titleText={i18n.deleteTopicsNonSelectedDialog_Heading}
            bodyText={i18n.deleteTopicsNonSelectedDialog_Text}
            confirmButtonType="primary"
            on:close={noTopicsModalClicked}
        />

        <ButtonDropdown
            iconPlacement="end"
            id="topics-actions-dropdown"
            items={getActions()}
            large={true}
            responsive={false}
            textOnly={false}
            text={i18n.actions}
            type="secondary"
            on:selectedOption={selectedAction}
        />
    {/if}
</div>

<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { get } from "svelte/store";
    import { isMobile } from "@itslearning/atlas/detectors/UserAgentHelper";
    import ButtonDropdown from "@itslearning/prometheus/assets/dropdowns/ButtonDropdown/v1/ButtonDropdown.svelte";
    import Checkbox from "@itslearning/prometheus/assets/inputs/Checkbox/v1/Checkbox.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import CopyConfirmAndErrorModal from "../CopyConfirmAndErrorModal/CopyConfirmAndErrorModal.svelte";
    import { copyTopics, deleteTopics } from "../../api.js";
    import config from "../../config";
    import { endpoints, topicsActionType } from "../../constants";
    import { getCoursesForCopy } from "../../controllers/updatePlanController";
    import { makeCourseForDropdown } from "../../helpers/makeCourseForDropdownHelper";
    import { isDateVisible } from "../../helpers/visibilityHelper";
    import i18n from "../../i18n.js";
    import { selectedTopicIds } from "../../logic/plans/stores";

    const dispatch = createEventDispatcher();

    export let showPrintAction = true;

    let showCopyTopicsModal = false;
    let showCopyConfirmModal = false;
    let showDeleteTopicsConfirmModal = false;
    let showNoTopicsSelectedModal = false;

    let body;
    let errorNoCourseSelected = false;
    let dropDownCourses = [];
    let dropDownCoursesLoading = true;
    let coursesItems;
    let courseIsSelected = false;
    let selectedCourseTitle = "";
    let selectedCourseId;
    let checkedCopyDates = false;
    let checkedConnectedResources = true; // IMPORTANT, checked by default
    let checkedAssessmentCriteria = false;
    let showCopyInProgress = false;
    let confirmModalBody;
    let confirmModalBannerVisibility = false;
    let confirmModalBannerText;

    $: coursesDropdownText = !courseIsSelected ? i18n.selectACourse : selectedCourseTitle;

    $: showAssessmentCriteriaSection =
        config.showAssessmentCriteriaSection &&
        selectedCourseId !== null &&
        selectedCourseId !== config.courseId;

    onMount(() => {
        getCoursesForCopy()
            .then(data => dropDownCourses = data)
            .finally(() => dropDownCoursesLoading = false);
    });


    async function selectedAction(event) {
        const topicIds = get(selectedTopicIds);

        const data = event.detail.data;

        switch (data.source) {
            case topicsActionType.Copy:
                if (topicIds && topicIds.size === 0) {
                    showNoTopicsSelectedModal = true;
                } else {
                    showCopyTopicsModal = true;
                    setTimeout(() => openModalInMobile(), 100);
                }

                break;
            case topicsActionType.Delete:
                if (topicIds && topicIds.size === 0) {
                    showNoTopicsSelectedModal = true;
                } else {
                    showDeleteTopicsConfirmModal = true;
                }

                break;
            case topicsActionType.Print:
                if (!topicIds || topicIds.size === 0) {
                    showNoTopicsSelectedModal = true;
                } else {
                    window.open(endpoints.printTopics(config.courseId, [...topicIds]));
                }

                break;
        }
    }

    /** Topic action options  **/
    function getActions() {
        const options = [];

        options.push({
            icon: "",
            label: i18n.copy,
            link: false,
            target: "_self",
            value: { source: topicsActionType.Copy }
        });

        if (showPrintAction) {
            options.push({
                icon: "",
                label: i18n.print,
                link: false,
                target: "_self",
                value: { source: topicsActionType.Print }
            });
        }

        options.push({
            icon: "",
            label: i18n.delete,
            link: false,
            target: "_self",
            value: { source: topicsActionType.Delete }
        });

        return [{ options }];
    }

    function loadCourses() {
        const starredCoursesList = [];
        const otherCoursesList = [];

        // Selects the arrays for starred and other courses, indicates which courses are archived
        dropDownCourses.forEach(course => {
            if (course.HasCourseSettingPlanner) {
                const courseForDropdown = makeCourseForDropdown(course, topicsActionType.Copy, i18n.archived);

                course.IsFavourite
                    ? starredCoursesList.push(courseForDropdown)
                    : otherCoursesList.push(courseForDropdown);
            }
        });

        /** Courses dropdown options  **/
        coursesItems = [
            {
                label: i18n.starred,
                options: starredCoursesList
            },
            {
                label: i18n.otherCourses,
                options: otherCoursesList
            }
        ];
    }

    function selectCourse(event) {
        const data = event.detail.data;

        if (data.source === topicsActionType.Copy) {
            errorNoCourseSelected = false;
            courseIsSelected = true;
            selectedCourseTitle = data.title;
            selectedCourseId = data.id;
        }
    }

    function updateCheckedStatusCopyDates() {
        if (checkedCopyDates) {
            checkedCopyDates = false;
        } else {
            checkedCopyDates = true;
        }
    }

    function updateCheckedStatusConnectedResources() {
        if (checkedConnectedResources) {
            checkedConnectedResources = false;
        } else {
            checkedConnectedResources = true;
        }
    }

    function updateCheckedStatusAssessmentCriteria() {
        if (checkedAssessmentCriteria) {
            checkedAssessmentCriteria = false;
        } else {
            checkedAssessmentCriteria = true;
        }
    }

    async function copyTopicsModalClicked(event) {
        if (event.detail.confirmed === true) {
            if (!courseIsSelected) {
                // If Copy is pressed, but no course is selected
                showCopyTopicsModal = true;
                errorNoCourseSelected = true;
            } else {
                showCopyInProgress = true;

                const topicIds = get(selectedTopicIds);

                if (topicIds && topicIds.size > 0) {
                    const copyTopicsData = await copyTopics(
                        selectedCourseId,
                        checkedCopyDates,
                        checkedConnectedResources,
                        checkedAssessmentCriteria,
                        [...topicIds]
                    );

                    confirmModalBody = copyTopicsData.ResultMessage;

                    if (copyTopicsData.ResultWarningNotes) {
                        // If there is a warning note, then it is displayed in the banner
                        confirmModalBannerVisibility = true;
                        confirmModalBannerText = copyTopicsData.ResultWarningNotes;
                    }
                }

                showCopyInProgress = false;
                showCopyConfirmModal = true;
            }
        } else {
            // On Cancel, the Topic page gets reloaded, to reset all parameters
            selectedTopicIds.set(new Set([]));
            dispatch("endOfActionOnSelectedTopics", { shouldUpdate: false });
        }
    }

    function closeCopyTopicsConfirmModalClicked() {
        showCopyConfirmModal = false;
        selectedTopicIds.set(new Set([]));
        dispatch("endOfActionOnSelectedTopics", { shouldUpdate: false });
    }

    async function deleteTopicsModalClicked(event) {
        if (event.detail.confirmed === true) {
            const topicIds = get(selectedTopicIds);

            if (topicIds && topicIds.size > 0) {
                await deleteTopics([...topicIds]);
                selectedTopicIds.set(new Set([]));
                dispatch("endOfActionOnSelectedTopics", { shouldUpdate: true });
            }
        }

        showDeleteTopicsConfirmModal = false;
    }

    function noTopicsModalClicked() {
        showNoTopicsSelectedModal = false;
    }

    const modalId = "#show-copy-topics-modal-id";

    function openModalInMobile() {
        if (isMobile()) {
            if (document.querySelector(modalId) !== null) {
                document.querySelector(modalId).style.minHeight = "100%";
            }
        }
    }

    window.addEventListener("orientationchange", function () {
        if (document.querySelector(modalId) !== null) {
            setTimeout(() => openModalInMobile(), 300);
        }
    });

    // Add a scroll bar to the courses dropdown in copy topic modal
    function addScrollCourseDropdownTopics() {
        const dropdownSection =
            "#copy-topics-modal__courses-dropdown > section > section.prom-dropdown__body";

        if (document.querySelector(dropdownSection).style.height === "") {
            document.querySelector(dropdownSection).style.height = "150px";
        }
    }
</script>
